import React, { useEffect, useState } from "react";
import { Marker, Circle, useMapEvents } from "react-leaflet";
import LeafletMap, { liveDotIcon, Recenter, useUserFallbackCenter } from "../components/LeafletMap";
import { api } from "../api";
import { useAuth } from "../auth";
import { useDevices } from "../devices";
import DevicePicker from "../components/DevicePicker";
import { Plus, Trash2, MapPin } from "lucide-react";

function TapHandler({ onTap }) {
  useMapEvents({ click: (e) => onTap([e.latlng.lat, e.latlng.lng]) });
  return null;
}

export default function Geofences() {
  const { t, formatApiError } = useAuth();
  const { selectedDeviceId } = useDevices();
  const deviceId = selectedDeviceId;
  const fallbackCenter = useUserFallbackCenter();
  const [fences, setFences] = useState([]);
  const [placing, setPlacing] = useState(null); // [lat,lng]
  const [radius, setRadius] = useState(500);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [center, setCenter] = useState(fallbackCenter);

  const load = async (id) => {
    const { data } = await api.get(`/geofence/${id}`);
    setFences(data);
  };

  useEffect(() => {
    if (!deviceId) { setFences([]); return; }
    (async () => {
      await load(deviceId);
      try {
        const { data: live } = await api.get(`/location/live/${deviceId}`);
        if (live?.last_location) {
          setCenter([live.last_location.latitude, live.last_location.longitude]);
          return; // got real GPS, no need for fallback
        }
      } catch (_) {}
      // Fall back to browser geolocation (via the shared hook)
      setCenter(fallbackCenter);
    })();
  }, [deviceId, fallbackCenter]);

  const save = async () => {
    if (!placing || !name || !deviceId) return;
    setSaving(true); setErr("");
    try {
      await api.post("/geofence", {
        device_id: deviceId, name,
        latitude: placing[0], longitude: placing[1], radius,
      });
      setPlacing(null); setName("");
      await load(deviceId);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await api.delete(`/geofence/${id}`);
    await load(deviceId);
  };

  return (
    <div data-testid="geofence-screen" className="h-[100vh] h-[100dvh] w-full relative overflow-hidden pb-24">
      <div className="absolute top-0 left-0 right-0 z-[450] px-4 pt-4">
        <div className="glass-card rounded-2xl px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h1 className="text-lg font-black text-slate-900">{t.safeZones}</h1>
              <p className="text-[11px] text-slate-500">{t.tapMapToPlace}</p>
            </div>
            <div className="flex items-center gap-2">
              <DevicePicker />
              <MapPin className="text-primary" size={22}/>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute inset-0">
        <LeafletMap center={center} zoom={14} testid="geofence-map">
          <Recenter lat={center[0]} lon={center[1]} zoom={14} />
          <TapHandler onTap={setPlacing} />
          {placing && (
            <>
              <Marker position={placing} icon={liveDotIcon} />
              <Circle center={placing} radius={radius}
                      pathOptions={{ color: "#1a56db", fillColor: "#1a56db", fillOpacity: 0.15 }} />
            </>
          )}
          {fences.map((f) => (
            <Circle key={f.id} center={[f.latitude, f.longitude]} radius={f.radius}
                    pathOptions={{ color: "#16a34a", fillColor: "#16a34a", fillOpacity: 0.1 }} />
          ))}
        </LeafletMap>
      </div>

      {/* Place card */}
      {placing && (
        <div
          className="absolute left-3 right-3 z-[450]"
          style={{ bottom: "calc(88px + env(safe-area-inset-bottom))" }}
        >
          <div data-testid="geofence-form" className="glass-card rounded-2xl p-4 space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {t.geofenceName}
              </label>
              <input
                data-testid="geofence-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Home / Office / School"
                className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-primary"
              />
            </div>
            <div>
              <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-slate-500">
                <span>{t.radius}</span>
                <span className="text-primary">{radius}m</span>
              </div>
              <input
                data-testid="geofence-radius"
                type="range" min="100" max="5000" step="50"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="w-full accent-primary"
              />
            </div>
            {err && <div className="text-xs text-parked">{err}</div>}
            <div className="flex gap-2">
              <button
                data-testid="geofence-cancel"
                onClick={() => setPlacing(null)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 font-semibold text-slate-600"
              >
                {t.cancel}
              </button>
              <button
                data-testid="geofence-save"
                onClick={save}
                disabled={!name || saving}
                className="flex-1 py-2.5 rounded-xl bg-primary text-white font-bold disabled:opacity-60"
              >
                {saving ? t.loading : t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {!placing && (
        <div
          className="absolute left-3 right-3 z-[450] max-h-[40vh] overflow-auto no-scrollbar"
          style={{ bottom: "calc(88px + env(safe-area-inset-bottom))" }}
        >
          <div className="glass-card rounded-2xl p-3" data-testid="geofence-list">
            {fences.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-400">{t.noGeofences}</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {fences.map((f) => (
                  <li key={f.id} className="flex items-center gap-3 py-2.5">
                    <div className="w-9 h-9 rounded-full bg-primary-light text-primary flex items-center justify-center">
                      <MapPin size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 truncate">{f.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {f.latitude.toFixed(4)}, {f.longitude.toFixed(4)} · {f.radius}m
                      </div>
                    </div>
                    <button
                      data-testid={`geofence-delete-${f.id}`}
                      onClick={() => remove(f.id)}
                      className="text-parked p-2 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {!placing && (
        <button
          data-testid="geofence-fab"
          onClick={() => setPlacing(center)}
          className="absolute right-4 z-[460] bg-primary text-white rounded-full w-14 h-14 flex items-center justify-center shadow-soft"
          style={{ bottom: "calc(180px + env(safe-area-inset-bottom))" }}
          title={t.placeGeofence}
        >
          <Plus size={26} />
        </button>
      )}
    </div>
  );
}
